'use client';

import {
  CaseStudyBody,
  CaseStudyResultsTable,
} from '@/components/case-study/case-study-body';
import { CaseStudyHero } from '@/components/case-study/case-study-hero';
import { CustomerVideos } from '@/components/sections/customer-videos';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const OCNCaseStudy = () => {
  const { t, isRTL } = useTranslation();
  return (
    <>
      <CaseStudyHero
        customerName="OneCar Now"
        title={
          t.customerStories[
            'How OCN automates time-sensitive car subscriptions across Mexico'
          ]
        }
        subtitle={
          t.customerStories[
            "Open's AI handles complex, time-sensitive workflows across email, WhatsApp, and web chat—from credit approvals to vehicle delivery coordination—at scale."
          ]
        }
        stats={[
          {
            value: isRTL ? '٧٥٠٠' : '7.5K',
            label: t.customerStories['Vehicles deployed'],
          },
          {
            value: isRTL ? '٦٠٪' : '60%',
            label: t.customerStories['Automation rate'],
          },
          {
            value: isRTL ? '٩٠ دقيقة' : '90min',
            label: t.customerStories['Avg response time'],
          },
        ]}
        videoSrc="/home/features/ocn.mp4"
        videoVariant="ambient"
      />

      <CaseStudyBody
        aside={{
          logoAlt: 'OneCar Now',
          customerName: 'OneCar Now',
          company:
            t.customerStories[
              'OneCar Now (OCN) is a Mexico-based fintech offering 100% online car subscriptions for gig economy workers, backed by $86M in Series A funding.'
            ],
          industry: t.customerStories['Fintech / Mobility'],
          websiteUrl: 'https://onecarnow.com',
          keyResults: [
            t.customerStories['• 60% automation rate'],
            t.customerStories['• 90min average response time'],
            t.customerStories['• 7,500 EVs managed at scale'],
            t.customerStories[
              '• Multi-channel automation (email, WhatsApp, web)'
            ],
          ],
        }}
      >
        <p>{t.customerStories['ocn_intro']}</p>

        <h2>{t.customerStories['ocn_challenge_title']}</h2>
        <p>{t.customerStories['ocn_challenge_p1']}</p>
        <ul>
          <li>
            <strong>{t.customerStories['Document verification']}</strong>{' '}
            {t.customerStories['that needs instant processing']}
          </li>
          <li>
            <strong>{t.customerStories['Credit approvals']}</strong>{' '}
            {t.customerStories['with strict time windows']}
          </li>
          <li>
            <strong>
              {t.customerStories['Vehicle delivery coordination']}
            </strong>{' '}
            {t.customerStories['across Mexico']}
          </li>
          <li>
            <strong>{t.customerStories['Multi-channel support']}</strong>{' '}
            {t.customerStories['via email, WhatsApp, and web chat']}
          </li>
          <li>
            <strong>{t.customerStories['Maintenance scheduling']}</strong>{' '}
            {t.customerStories['for thousands of vehicles']}
          </li>
        </ul>
        <p>{t.customerStories['ocn_challenge_p2']}</p>

        <h2>{t.customerStories['ocn_solution_title']}</h2>
        <p>{t.customerStories['ocn_solution_p1']}</p>

        <CaseStudyResultsTable
          metricLabel={t.customerStories['Channel']}
          impactLabel={t.customerStories['Automated Actions']}
          rows={[
            {
              metric: t.customerStories['Email'],
              impact:
                t.customerStories[
                  'Document verification, application status updates'
                ],
            },
            {
              metric: t.customerStories['WhatsApp'],
              impact:
                t.customerStories['Delivery scheduling, payment confirmations'],
            },
            {
              metric: t.customerStories['Web Chat'],
              impact:
                t.customerStories[
                  'Real-time application support, troubleshooting'
                ],
            },
          ]}
        />

        <h2>{t.customerStories['ocn_results_title']}</h2>
        <p>{t.customerStories['ocn_results_p1']}</p>
        <ul>
          <li>
            <strong>
              {t.customerStories['60% of inquiries fully automated']}
            </strong>{' '}
            {t.customerStories['across all channels']}
          </li>
          <li>
            <strong>
              {t.customerStories['90-minute average response time']}
            </strong>{' '}
            {t.customerStories['down from several hours']}
          </li>
          <li>
            <strong>{t.customerStories['7,500 vehicles managed']}</strong>{' '}
            {t.customerStories['with the same support team size']}
          </li>
          <li>
            <strong>{t.customerStories['Complex workflows automated']}</strong>{' '}
            {
              t.customerStories[
                'credit checks, delivery coordination, document processing'
              ]
            }
          </li>
        </ul>
        <p>{t.customerStories['ocn_results_p2']}</p>

        <h2>{t.customerStories['ocn_why_title']}</h2>
        <p>{t.customerStories['ocn_why_p1']}</p>
        <ul>
          <li>
            {
              t.customerStories[
                "Process uploaded driver's licenses and insurance"
              ]
            }
          </li>
          <li>
            {t.customerStories['Update credit approval status in real-time']}
          </li>
          <li>
            {
              t.customerStories[
                'Schedule vehicle pickups across different cities'
              ]
            }
          </li>
          <li>{t.customerStories['Send payment reminders via WhatsApp']}</li>
          <li>{t.customerStories['Coordinate maintenance appointments']}</li>
        </ul>
        <p>{t.customerStories['ocn_why_p2']}</p>

        <h2>{t.customerStories['ocn_scaling_title']}</h2>
        <p>{t.customerStories['ocn_scaling_p1']}</p>
        <p>{t.customerStories['ocn_scaling_p2']}</p>

        <h2>{t.customerStories['ocn_future_title']}</h2>
        <p>{t.customerStories['ocn_future_p1']}</p>
        <p>{t.customerStories['ocn_future_p2']}</p>
      </CaseStudyBody>

      <CustomerVideos />

      <Testimonials />
    </>
  );
};

export default OCNCaseStudy;
